'use client';

import React, { useState, useMemo } from 'react';
import { Search, Filter, Plus, Eye, Receipt, CheckSquare } from 'lucide-react';
import Link from 'next/link';
import StatusBadge from '@/components/ui/StatusBadge';
import SlideOverPanel from '@/components/ui/SlideOverPanel';
import InvoiceDetailPanel from './InvoiceDetailPanel';
import { toast } from 'sonner';

export type Invoice = {
  id: string;
  invoiceNumber: string;
  type: 'AR' | 'AP';
  jobId: string;
  jobName: string;
  counterparty: string;
  issueDate: string;
  dueDate: string;
  amount: string;
  amountRaw: number;
  status: string;
  daysLabel: string;
  isOverdue: boolean;
  linkedDoc: string;
};

export const invoiceData: Invoice[] = [
  { id: 'inv-10482', invoiceNumber: 'INV-10482', type: 'AR', jobId: 'JOB-CRM-9924', jobName: 'Merch drop — TechConf 2026', counterparty: 'TechConf Events Ltd', issueDate: '10/08/2026', dueDate: '10/08/2026', amount: '£8,400', amountRaw: 8400, status: 'overdue', daysLabel: '11 days overdue', isOverdue: true, linkedDoc: 'SO-3243' },
  { id: 'inv-10481', invoiceNumber: 'INV-10481', type: 'AR', jobId: 'JOB-CRM-9921', jobName: 'Branded hoodies — RetailCo Q3', counterparty: 'RetailCo UK', issueDate: '14/08/2026', dueDate: '22/08/2026', amount: '£14,220', amountRaw: 14220, status: 'due', daysLabel: 'Due tomorrow', isOverdue: false, linkedDoc: 'SO-3238' },
  { id: 'inv-10480', invoiceNumber: 'INV-10480', type: 'AR', jobId: 'JOB-CRM-9917', jobName: 'Tote bags — GreenFest', counterparty: 'GreenFest CIC', issueDate: '01/08/2026', dueDate: '18/08/2026', amount: '£3,150', amountRaw: 3150, status: 'paid', daysLabel: 'Paid 18 Aug', isOverdue: false, linkedDoc: 'SO-3225' },
  { id: 'inv-10479', invoiceNumber: 'INV-10479', type: 'AP', jobId: 'JOB-CRM-9924', jobName: 'Merch drop — TechConf 2026', counterparty: 'PrintFast UK', issueDate: '16/08/2026', dueDate: '30/08/2026', amount: '£6,800', amountRaw: 6800, status: 'sent', daysLabel: 'Due in 9 days', isOverdue: false, linkedDoc: 'PO-2853' },
  { id: 'inv-10478', invoiceNumber: 'INV-10478', type: 'AR', jobId: 'JOB-CRM-9915', jobName: 'Embroidered polos — HealthOrg', counterparty: 'HealthOrg NHS', issueDate: '08/08/2026', dueDate: '28/08/2026', amount: '£11,640', amountRaw: 11640, status: 'sent', daysLabel: 'Due in 7 days', isOverdue: false, linkedDoc: 'SO-3218' },
  { id: 'inv-10477', invoiceNumber: 'INV-10477', type: 'AP', jobId: 'JOB-CRM-9921', jobName: 'Branded hoodies — RetailCo Q3', counterparty: 'Gildan Activewear EU', issueDate: '14/08/2026', dueDate: '14/08/2026', amount: '£9,600', amountRaw: 9600, status: 'overdue', daysLabel: '7 days overdue', isOverdue: true, linkedDoc: 'PO-2848' },
  { id: 'inv-10476', invoiceNumber: 'INV-10476', type: 'AR', jobId: 'JOB-CRM-9912', jobName: 'Caps and beanies — SportsBrand', counterparty: 'SportsBrand Ltd', issueDate: '04/08/2026', dueDate: '04/08/2026', amount: '£6,800', amountRaw: 6800, status: 'overdue', daysLabel: '17 days overdue', isOverdue: true, linkedDoc: 'SO-3211' },
  { id: 'inv-10475', invoiceNumber: 'INV-10475', type: 'AP', jobId: 'JOB-CRM-9915', jobName: 'Embroidered polos — HealthOrg', counterparty: 'Gildan Activewear EU', issueDate: '06/08/2026', dueDate: '20/08/2026', amount: '£14,200', amountRaw: 14200, status: 'paid', daysLabel: 'Paid 20 Aug', isOverdue: false, linkedDoc: 'PO-2835' },
  { id: 'inv-10474', invoiceNumber: 'INV-10474', type: 'AR', jobId: 'JOB-CRM-9908', jobName: 'Desk kits — FinanceCorp', counterparty: 'FinanceCorp PLC', issueDate: '29/07/2026', dueDate: '12/08/2026', amount: '£11,760', amountRaw: 11760, status: 'reconciled', daysLabel: 'Reconciled', isOverdue: false, linkedDoc: 'SO-3201' },
  { id: 'inv-10473', invoiceNumber: 'INV-10473', type: 'AP', jobId: 'JOB-CRM-9919', jobName: 'Promo kit — SaaS Summit', counterparty: 'Moleskine Trade', issueDate: '12/08/2026', dueDate: '26/08/2026', amount: '£9,400', amountRaw: 9400, status: 'sent', daysLabel: 'Due in 5 days', isOverdue: false, linkedDoc: 'PO-2843' },
  { id: 'inv-10472', invoiceNumber: 'INV-10472', type: 'AR', jobId: 'JOB-CRM-9904', jobName: 'Branded water bottles — FitLife', counterparty: 'FitLife Gyms', issueDate: '23/07/2026', dueDate: '06/08/2026', amount: '£8,640', amountRaw: 8640, status: 'reconciled', daysLabel: 'Reconciled', isOverdue: false, linkedDoc: 'SO-3196' },
  { id: 'inv-10471', invoiceNumber: 'INV-10471', type: 'AP', jobId: 'JOB-CRM-9912', jobName: 'Caps and beanies — SportsBrand', counterparty: 'Headwear Direct', issueDate: '02/08/2026', dueDate: '16/08/2026', amount: '£7,800', amountRaw: 7800, status: 'paid', daysLabel: 'Paid 16 Aug', isOverdue: false, linkedDoc: 'PO-2831' },
];

const statusOptions = ['All statuses', 'Draft', 'Sent', 'Due', 'Overdue', 'Paid', 'Reconciled'];

export default function InvoiceContent() {
  const [activeTab, setActiveTab] = useState<'all' | 'AR' | 'AP'>('all');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('All statuses');
  const [selectedInvoices, setSelectedInvoices] = useState<Set<string>>(new Set());
  const [selectedInvoice, setSelectedInvoice] = useState<Invoice | null>(null);
  const [panelOpen, setPanelOpen] = useState(false);

  const filtered = useMemo(() => {
    return invoiceData.filter(inv => {
      const matchTab = activeTab === 'all' || inv.type === activeTab;
      const matchSearch = !search ||
        inv.invoiceNumber.toLowerCase().includes(search.toLowerCase()) ||
        inv.counterparty.toLowerCase().includes(search.toLowerCase()) ||
        inv.jobName.toLowerCase().includes(search.toLowerCase());
      const matchStatus = statusFilter === 'All statuses' || inv.status === statusFilter.toLowerCase();
      return matchTab && matchSearch && matchStatus;
    });
  }, [activeTab, search, statusFilter]);

  const toggleRow = (id: string) => {
    setSelectedInvoices(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    if (selectedInvoices.size === filtered.length) {
      setSelectedInvoices(new Set());
    } else {
      setSelectedInvoices(new Set(filtered.map(i => i.id)));
    }
  };

  const handleBulkMarkPaid = () => {
    // Backend integration point: PATCH /api/invoices/bulk-status { ids, status: 'paid' }
    toast.success(`${selectedInvoices.size} invoice${selectedInvoices.size > 1 ? 's' : ''} marked as paid`);
    setSelectedInvoices(new Set());
  };

  const handleBulkReconcile = () => {
    // Backend integration point: POST /api/reconciliation/bulk { invoiceIds: [...] }
    toast.success(`${selectedInvoices.size} invoice${selectedInvoices.size > 1 ? 's' : ''} queued for reconciliation`);
    setSelectedInvoices(new Set());
  };

  const openInvoice = (inv: Invoice) => {
    setSelectedInvoice(inv);
    setPanelOpen(true);
  };

  const arTotal = invoiceData.filter(i => i.type === 'AR' && !['paid', 'reconciled'].includes(i.status)).reduce((s, i) => s + i.amountRaw, 0);
  const apTotal = invoiceData.filter(i => i.type === 'AP' && !['paid', 'reconciled'].includes(i.status)).reduce((s, i) => s + i.amountRaw, 0);
  const overdueCount = invoiceData.filter(i => i.status === 'overdue').length;

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-semibold" style={{ color: 'var(--foreground)' }}>Invoices</h1>
          <p className="text-sm mt-0.5" style={{ color: 'var(--slate)' }}>
            AR/AP invoice management — {overdueCount} overdue requiring attention
          </p>
        </div>
        <button
          onClick={() => toast.info('Create invoice — connect to POST /api/invoices')}
          className="flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-md text-white transition-all active:scale-95"
          style={{ backgroundColor: 'var(--accent)' }}
        >
          <Plus size={15} />
          Create invoice
        </button>
      </div>

      {/* Summary strip */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {[
          { label: 'AR outstanding', value: `£${arTotal.toLocaleString('en-GB')}`, color: 'var(--foreground)', bg: 'var(--card)' },
          { label: 'AP owed', value: `£${apTotal.toLocaleString('en-GB')}`, color: 'var(--foreground)', bg: 'var(--card)' },
          { label: 'Overdue invoices', value: String(overdueCount), color: '#B91C1C', bg: '#FEF2F2' },
        ].map(s => (
          <div
            key={`inv-summary-${s.label}`}
            className="rounded-lg border px-4 py-3 flex items-center justify-between"
            style={{ backgroundColor: s.bg, borderColor: s.bg === '#FEF2F2' ? '#FECACA' : 'var(--border)' }}
          >
            <span className="text-xs font-medium" style={{ color: 'var(--slate)' }}>{s.label}</span>
            <span className="text-lg font-bold tabular-nums" style={{ color: s.color }}>{s.value}</span>
          </div>
        ))}
      </div>

      {/* Tabs + filters */}
      <div
        className="rounded-xl border"
        style={{ backgroundColor: 'var(--card)', borderColor: 'var(--border)' }}
      >
        {/* Tab row */}
        <div
          className="flex items-center justify-between px-4 pt-3 pb-0 border-b"
          style={{ borderColor: 'var(--border)' }}
        >
          <div className="flex gap-1">
            {(['all', 'AR', 'AP'] as const).map(t => (
              <button
                key={`inv-tab-${t}`}
                onClick={() => setActiveTab(t)}
                className="px-4 py-2 text-sm font-medium border-b-2 transition-colors"
                style={{
                  borderColor: activeTab === t ? 'var(--accent)' : 'transparent',
                  color: activeTab === t ? 'var(--accent)' : 'var(--slate)',
                }}
              >
                {t === 'all' ? 'All invoices' : t === 'AR' ? 'Accounts receivable' : 'Accounts payable'}
              </button>
            ))}
          </div>
        </div>

        {/* Filter row */}
        <div className="flex flex-wrap items-center gap-3 p-4 border-b" style={{ borderColor: 'var(--border)' }}>
          <div
            className="flex items-center gap-2 flex-1 min-w-[200px] px-3 py-2 rounded-md border"
            style={{ borderColor: 'var(--border)', backgroundColor: 'var(--cloud)' }}
          >
            <Search size={14} style={{ color: 'var(--slate)' }} />
            <input
              type="text"
              placeholder="Search invoice number, client, or job…"
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="flex-1 text-sm outline-none bg-transparent"
              style={{ color: 'var(--foreground)' }}
            />
          </div>
          <div className="relative">
            <Filter size={13} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--slate)' }} />
            <select
              value={statusFilter}
              onChange={e => setStatusFilter(e.target.value)}
              className="pl-8 pr-3 py-2 text-sm rounded-md border outline-none appearance-none cursor-pointer"
              style={{ borderColor: 'var(--border)', backgroundColor: 'var(--cloud)', color: 'var(--foreground)' }}
            >
              {statusOptions.map(s => <option key={`inv-status-opt-${s}`}>{s}</option>)}
            </select>
          </div>
          <span className="text-xs ml-auto" style={{ color: 'var(--slate)' }}>
            {filtered.length} invoices
          </span>
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr style={{ borderBottom: '1px solid var(--border)', backgroundColor: 'var(--cloud)' }}>
                <th className="px-4 py-3 w-10">
                  <input
                    type="checkbox"
                    checked={selectedInvoices.size === filtered.length && filtered.length > 0}
                    onChange={toggleAll}
                    className="w-4 h-4 rounded"
                    style={{ accentColor: 'var(--accent)' }}
                  />
                </th>
                {['Invoice #', 'Type', 'Job', 'Client / Supplier', 'Issued', 'Due date', 'Amount', 'Status', 'Days', ''].map((h, i) => (
                  <th
                    key={`inv-th-${i}`}
                    className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider whitespace-nowrap"
                    style={{ color: 'var(--slate)', letterSpacing: '0.05em' }}
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan={10} className="text-center py-16">
                    <Receipt size={32} className="mx-auto mb-3" style={{ color: 'var(--border)' }} />
                    <p className="text-sm font-medium" style={{ color: 'var(--foreground)' }}>No invoices match your filters</p>
                    <p className="text-xs mt-1" style={{ color: 'var(--slate)' }}>Invoices are generated from sales orders and purchase orders</p>
                  </td>
                </tr>
              ) : (
                filtered.map(inv => (
                  <tr
                    key={inv.id}
                    className="border-b cursor-pointer transition-colors"
                    style={{
                      borderColor: 'var(--border)',
                      backgroundColor: inv.isOverdue
                        ? selectedInvoices.has(inv.id) ? '#fde8c4' : 'var(--amber-light)'
                        : selectedInvoices.has(inv.id) ? 'var(--teal-light)' : undefined,
                    }}
                    onClick={() => openInvoice(inv)}
                  >
                    <td className="px-4 py-3" onClick={e => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        checked={selectedInvoices.has(inv.id)}
                        onChange={() => toggleRow(inv.id)}
                        className="w-4 h-4 rounded"
                        style={{ accentColor: 'var(--accent)' }}
                      />
                    </td>
                    <td className="px-4 py-3">
                      <Link
                        href={`/invoices/${inv.id}`}
                        onClick={e => e.stopPropagation()}
                        className="text-xs font-mono font-semibold hover:underline"
                        style={{ color: 'var(--accent)' }}
                      >
                        {inv.invoiceNumber}
                      </Link>
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className="text-xs font-semibold px-1.5 py-0.5 rounded"
                        style={{
                          backgroundColor: inv.type === 'AR' ? 'var(--teal-light)' : 'var(--slate-light)',
                          color: inv.type === 'AR' ? 'var(--accent)' : 'var(--slate)',
                        }}
                      >
                        {inv.type}
                      </span>
                    </td>
                    <td className="px-4 py-3 max-w-[160px]">
                      <p className="text-xs font-mono" style={{ color: 'var(--slate)' }}>{inv.jobId}</p>
                      <p className="text-sm truncate font-medium" style={{ color: 'var(--foreground)' }}>{inv.jobName}</p>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span style={{ color: 'var(--foreground)' }}>{inv.counterparty}</span>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span style={{ color: 'var(--slate)' }}>{inv.issueDate}</span>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span
                        style={{ color: inv.isOverdue ? '#B91C1C' : 'var(--slate)', fontWeight: inv.isOverdue ? 600 : 400 }}
                      >
                        {inv.dueDate}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="tabular-nums font-semibold" style={{ color: 'var(--foreground)' }}>{inv.amount}</span>
                    </td>
                    <td className="px-4 py-3">
                      <StatusBadge status={inv.status as never} pulse={inv.isOverdue} />
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span
                        className="text-xs font-medium"
                        style={{ color: inv.isOverdue ? '#B91C1C' : inv.status === 'due' ? '#B87A1A' : 'var(--slate)' }}
                      >
                        {inv.daysLabel}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1">
                        <button
                          className="p-1.5 rounded-md hover:bg-muted transition-colors"
                          onClick={e => { e.stopPropagation(); openInvoice(inv); }}
                          aria-label="View invoice panel"
                        >
                          <Eye size={14} style={{ color: 'var(--slate)' }} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div
          className="flex items-center justify-between px-4 py-3 border-t"
          style={{ borderColor: 'var(--border)' }}
        >
          <p className="text-xs" style={{ color: 'var(--slate)' }}>
            Showing {filtered.length} of {invoiceData.length} invoices
          </p>
          <div className="flex items-center gap-1">
            {[1, 2].map(p => (
              <button
                key={`inv-page-${p}`}
                className="w-7 h-7 text-xs rounded-md font-medium"
                style={{ backgroundColor: p === 1 ? 'var(--ink)' : 'transparent', color: p === 1 ? 'white' : 'var(--slate)' }}
              >
                {p}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs" style={{ color: 'var(--slate)' }}>Rows per page:</span>
            <select
              className="text-xs px-2 py-1 rounded-md border outline-none"
              style={{ borderColor: 'var(--border)', backgroundColor: 'var(--cloud)', color: 'var(--foreground)' }}
            >
              {['15', '25', '50'].map(v => <option key={`inv-rows-${v}`}>{v}</option>)}
            </select>
          </div>
        </div>
      </div>

      {/* Bulk action bar */}
      {selectedInvoices.size > 0 && (
        <div
          className="fixed bottom-6 left-1/2 -translate-x-1/2 flex items-center gap-3 px-5 py-3 rounded-xl shadow-xl border z-30 fade-in"
          style={{ backgroundColor: 'var(--ink)', borderColor: 'rgba(255,255,255,0.1)' }}
        >
          <span className="text-sm font-medium text-white">
            {selectedInvoices.size} selected
          </span>
          <div className="w-px h-4" style={{ backgroundColor: 'rgba(255,255,255,0.2)' }} />
          <button
            onClick={handleBulkMarkPaid}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md transition-all"
            style={{ backgroundColor: 'rgba(18,148,107,0.3)', color: '#4ade80' }}
          >
            <CheckSquare size={13} />
            Mark as paid
          </button>
          <button
            onClick={handleBulkReconcile}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md transition-all"
            style={{ backgroundColor: 'rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.8)' }}
          >
            Reconcile
          </button>
          <button
            onClick={() => setSelectedInvoices(new Set())}
            className="text-xs"
            style={{ color: 'rgba(255,255,255,0.4)' }}
          >
            Clear
          </button>
        </div>
      )}

      {/* Detail panel */}
      <SlideOverPanel
        open={panelOpen}
        onClose={() => setPanelOpen(false)}
        title={selectedInvoice?.invoiceNumber ?? ''}
        subtitle={selectedInvoice?.jobName}
        width="600px"
      >
        {selectedInvoice && <InvoiceDetailPanel invoice={selectedInvoice} />}
      </SlideOverPanel>
    </div>
  );
}